"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { htmlLang, languageDirection, type UiLocale } from "@/lib/i18n/languages";
import type { MessageKey } from "@/lib/i18n/messages";
import { createUiTranslator, type UiDictionary } from "@/lib/i18n/uiRuntime";
import { localeForPath } from "@/lib/i18n/resolveUiLocale";
import { marketingLocaleFromPath } from "@/lib/i18n/marketingLocale";
import { StaticStringLocalizer } from "./StaticStringLocalizer";

interface I18nContextValue {
  locale: UiLocale;
  t: (key: MessageKey, values?: Record<string, string | number>) => string;
  translateSource: (source: string) => string | undefined;
  translateMarketing: (source: string) => string;
  setLocale: (locale: UiLocale) => Promise<boolean>;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  initialLocale,
  accountLocale,
  initialDictionary,
  children,
}: {
  initialLocale: UiLocale;
  accountLocale?: UiLocale;
  initialDictionary: UiDictionary;
  children: React.ReactNode;
}) {
  const pathname = usePathname() ?? "";
  // In-app switcher updates persist locally until the layout's accountLocale
  // catches up. Marketing URLs ignore this so /ja cannot leak into /dashboard.
  const [userOverride, setUserOverride] = useState<UiLocale | null>(null);
  const resolvedAccount = userOverride ?? accountLocale ?? initialLocale;
  const requestedLocale = pathname
    ? localeForPath(pathname, resolvedAccount)
    : initialLocale;
  const requestedScope = pathname && marketingLocaleFromPath(pathname) ? "messages" : "full";
  const [dictionary, setDictionary] = useState(initialDictionary);
  const cache = useRef(new Map<string, UiDictionary>([[`${initialDictionary.locale}:${initialDictionary.scope}`, initialDictionary]]));
  const loadDictionary = useCallback(async (nextLocale: UiLocale, scope: UiDictionary["scope"]) => {
    const key = `${nextLocale}:${scope}`;
    const cached = cache.current.get(key);
    if (cached) return cached;
    const response = await fetch(`/api/i18n/${nextLocale}${scope === "messages" ? "?scope=messages" : ""}`);
    if (!response.ok) throw new Error("Could not load interface language");
    const next = await response.json() as UiDictionary;
    if (next.locale !== nextLocale || next.scope !== scope) throw new Error("Unexpected interface language");
    cache.current.set(key, next);
    return next;
  }, []);
  // Marketing client navigation does not remount the root layout. Keep the
  // current interface usable until the destination language is available.
  useEffect(() => {
    let cancelled = false;
    cache.current.set(`${initialDictionary.locale}:${initialDictionary.scope}`, initialDictionary);
    void loadDictionary(requestedLocale, requestedScope).then(next => {
      if (!cancelled) setDictionary(next);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [requestedLocale, requestedScope, initialDictionary, loadDictionary]);
  const locale = dictionary.locale;

  useEffect(() => {
    document.documentElement.lang = htmlLang(locale);
    document.documentElement.dir = languageDirection(locale);
  }, [locale]);

  const setLocale = useCallback(async (nextLocale: UiLocale) => {
    if (nextLocale === locale) return true;
    try {
      const next = await loadDictionary(nextLocale, requestedScope);
      const response = await fetch("/api/user/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferredLocale: nextLocale }),
      });
      if (!response.ok) return false;
      setUserOverride(nextLocale);
      setDictionary(next);
      return true;
    } catch { return false; }
  }, [locale, loadDictionary, requestedScope]);

  const translator = useMemo(() => createUiTranslator(dictionary), [dictionary]);
  const value = useMemo<I18nContextValue>(() => ({
    locale,
    ...translator,
    setLocale,
  }), [locale, translator, setLocale]);

  return <I18nContext.Provider value={value}><StaticStringLocalizer />{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
